import { memo, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  CaseFacilityAddress,
  CaseFacilityAutocomplete,
  getRenderFacilityAutocompleteItem,
} from '@features/components/CaseFormBlocks/components';
import { CaseTxCenterFormType } from '@lp/features/CaseOverview/components/Tabs/CaseDetails/components/EditCaseForms/EditCaseTxCenter/config';
import { TXCenter } from '@/api/apis/facilities';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { getCountriesFilterByServiceRegion } from '@/utils/helpers';
import { ServiceRegionId } from '@/constants/serviceRegion.constants';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { getLocationPayload } from '../utils';

type TxDisabledState = {
  txCenterId?: boolean;
  txCenterAddress?: boolean;
};

type TxCenterSelectorProps = {
  disabled?: TxDisabledState;
  hasPermission?: boolean;
};

export const TxCenterSelector = memo(({ disabled, hasPermission }: TxCenterSelectorProps) => {
  const { control, setValue, trigger } = useFormContext<CaseTxCenterFormType>();
  const [txCenterId, txCenterName, serviceRegionId] = useWatch({
    control,
    name: ['txCenterId', 'txCenterName', 'serviceRegionId'],
  });

  const regionId = serviceRegionId as ServiceRegionId;
  const { countriesByIso2, resolveSubdivisionName } = useCountriesData();

  const handleTxCenterSelect = useCallback(
    async (item?: TXCenter) => {
      if (txCenterId === item?.id) return;

      const { location } = item ?? {};

      setValue('txCenterName', item?.name ?? '', { shouldDirty: true });
      setValue('txCenterCustomLocation', getLocationPayload(location, resolveSubdivisionName), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('txCenterTrialPrograms', item?.trialPrograms ?? [], {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('recipientHospitalDate', null, { shouldDirty: true });
      setValue('recipientHospitalTime', null, { shouldDirty: true });
      setValue('recipientHospitalTimeZone', location?.timezone, { shouldDirty: true });
      setValue('txEtaHospitalTimeZone', location?.timezone, { shouldDirty: true });
      setValue('coordinatorId', '', { shouldDirty: true, shouldValidate: true });
      setValue('receivingSurgeonId', '', { shouldDirty: true, shouldValidate: true });
      setValue('isServiceWithoutCoordinator', false, { shouldDirty: true });
      setValue('recipientHospitalORChangeReason', '', { shouldDirty: true });

      await trigger(['txCenterId', 'txCenterCustomLocation', 'coordinatorId']);
    },
    [setValue, trigger, txCenterId, resolveSubdivisionName],
  );
  const facilityFilters = useMemo(
    () => ({ countries: getCountriesFilterByServiceRegion(regionId, countriesByIso2) }),
    [regionId, countriesByIso2],
  );
  const renderListItem = useCallback(
    (item: object) => getRenderFacilityAutocompleteItem(resolveSubdivisionName)(item as TXCenter),
    [resolveSubdivisionName],
  );

  return (
    <div className="flex gap-4">
      <div className="flex-[50%]">
        <CaseFacilityAutocomplete
          key={serviceRegionId}
          isDisabled={disabled?.txCenterId || !hasPermission}
          name="txCenterId"
          control={control}
          placeholder="Select TX Center"
          ariaLabel="Select TX Center"
          infiniteListQuery={useInfiniteTXCentersListQuery}
          renderListItem={renderListItem}
          onSelect={handleTxCenterSelect}
          defaultInputValue={txCenterName}
          filters={facilityFilters}
        />
      </div>
      <div className="flex-[50%]">
        <CaseFacilityAddress
          disabled={disabled?.txCenterAddress || !hasPermission}
          fieldName="txCenterCustomLocation"
          label="TX Center Address"
        />
      </div>
    </div>
  );
});
